import { useEffect, useState } from "react";
import { ProgramType } from "data/programData";
import * as _ from "lodash";
import { useRecoilValue } from "recoil";
import { taskListAtom } from "recoil/taskListAtom";
import { Content_2_2_T } from "data/task/v1/2-2-T";
import { setDataWithEditorKeyAndTranslationVersion } from "./useGetBlankTaskData";
import { deviceAtom } from "recoil/deviceAtom";
import useIsInappWebview from "./useIsInappWebview";
import {
  ThoughtRecordTemplate,
  ThoughtTrapType,
} from "data/task/v1/thoughtRecord";
import { MeditationType } from "data/task/v1/5-7-T";
import { SentimentType } from "api/thoughtRecordApi";

export default function useGetBlankThoughtRecordData({
  trk,
  isFirst,
  isFromMeditationRecord,
  sentiment,
  enabled = true,
}: {
  trk?: string;
  isFirst?: boolean;
  isFromMeditationRecord?: boolean;
  sentiment?: SentimentType;
  enabled?: boolean;
}) {
  const taskId = "2-2-T";
  const [data, setData] = useState<ProgramType>();
  const deviceState = useRecoilValue(deviceAtom);
  const taskListState = useRecoilValue(taskListAtom);
  const isInappWebview = useIsInappWebview();
  const isPushNotificationGranted =
    deviceState.pushNotificationStatus === "granted" || !isInappWebview;

  const isSituationUpgraded = taskListState.some(
    (element) => element.taskId === "9-4-A" && element.isDone
  );
  const isEmotionUpgraded = taskListState.some(
    (element) => element.taskId === "9-6-A" && element.isDone
  );
  const isPhysicalResponseUpgraded = taskListState.some(
    (element) => element.taskId === "9-8-A" && element.isDone
  );
  const isBehaviorUpgraded = taskListState.some(
    (element) => element.taskId === "9-10-A" && element.isDone
  );
  const isWillingnessAvailable = taskListState.some(
    (element) => element.taskId === "9-13-A" && element.isDone
  );

  function ifTaskDone(taskId: string) {
    return taskListState.find(
      (element) => element.taskId === taskId && element.isDone
    );
  }

  function ifTaskDoneAndClose(taskId: string) {
    return taskListState.find(
      (element) =>
        element.taskId.startsWith(taskId) && element.isDone && !element.isOpen
    );
  }

  const meditationList = [
    ...(ifTaskDone("8-8-A") ? ["grounding"] : []),
    ...(ifTaskDone("8-8-A") ? ["relaxation"] : []),
    ...(ifTaskDone("8-9-A") ? ["breath"] : []),
    ...(ifTaskDone("9-13-A") ? ["willingness"] : []),
  ] as MeditationType[];

  const thoughtTrapList = [
    ...(ifTaskDoneAndClose("4-5-1-A") ? ["책임 과다"] : []),
    ...(ifTaskDoneAndClose("4-5-2-A") ? ["근거 부족"] : []),
    ...(ifTaskDoneAndClose("4-5-3-A") ? ["부정 편향"] : []),
    ...(ifTaskDoneAndClose("4-5-4-A") ? ["평가"] : []),
    ...(ifTaskDoneAndClose("4-5-5-A") ? ["개념화"] : []),
    ...(ifTaskDoneAndClose("4-5-6-A") ? ["반추"] : []),
  ] as ThoughtTrapType[];

  const thoughtRecordTaskData: ProgramType = {
    taskId: taskId,
    content: Content_2_2_T(isPushNotificationGranted, trk),
  };

  useEffect(() => {
    if (enabled) {
      let rawData = _.cloneDeep(thoughtRecordTaskData);
      if (trk && rawData) {
        if (!isFirst) {
          rawData.content = _.cloneDeep(
            ThoughtRecordTemplate({
              trk,
              sentiment,
              isSituationUpgraded,
              isEmotionUpgraded,
              isPhysicalResponseUpgraded,
              isBehaviorUpgraded,
              isWillingnessAvailable,
              isFromMeditationRecord,
              meditationList,
              includeLastBlock: true,
              thoughtTrapList: thoughtTrapList,
            })
          );
        }

        // Pre-select sentiment button if sentiment parameter is provided
        if (sentiment === "positive") {
          const sentimentBlockIndex = rawData.content.findIndex((element) =>
            element.lines
              .flat()
              .find((element) => element.content.id === "sentiment")
          );

          if (
            sentimentBlockIndex > -1 &&
            rawData.content[sentimentBlockIndex] &&
            rawData.content[sentimentBlockIndex].lines
          ) {
            const lineIndex = rawData.content[
              sentimentBlockIndex
            ].lines.findIndex((element) =>
              element
                .flat()
                .some((element) => element.content.id === "sentiment")
            );
            const cellIndex = 0;
            if (
              rawData.content[sentimentBlockIndex].lines[lineIndex] &&
              rawData.content[sentimentBlockIndex].lines[lineIndex][cellIndex]
            ) {
              const originalContent =
                rawData.content[sentimentBlockIndex].lines[lineIndex][cellIndex]
                  .content;
              rawData.content[sentimentBlockIndex].lines[lineIndex][
                cellIndex
              ].content = {
                ...originalContent,
                ...{ selectedIndex: 1, value: "좋은 일이에요" },
              };
            }
          }
        }

        setData(setDataWithEditorKeyAndTranslationVersion(rawData));
      }
    }
  }, [trk, enabled, sentiment]);

  return data;
}
